import moment, { Moment } from 'moment-timezone'
import i18next from 'i18next'
import { LatestContractDtoFragment, FullMeetingFragment, MeetingType } from '../../../types'
import { ContractChangeWithObjectPayload, ContractChangePayload } from './types'

interface AvailableMonths {
  month: number
  text: string
  year: number
}

export const generateAvailableMonths = (
  hasNationalContractReferenceLevel: boolean,
  hasGovernmentJobTitleReferenceLevel: boolean,
  utcDateStringToMoment: (utcDateString: string) => Moment | undefined,
  meeting: FullMeetingFragment,
  currentContract: LatestContractDtoFragment,
  scheduledChanges: string,
  change: ContractChangeWithObjectPayload,
  datesOfCurrentChanges: Date[],
  editedDate?: string,
): AvailableMonths[] => {
  const employeeTimezone = meeting.employee.branchOffice.timezone
  const currentDate = new Date()
  const currentYear = currentDate.getUTCFullYear()
  const currentMonth = currentDate.getUTCMonth()
  const nextYear = currentYear + 1
  const previousYear = currentYear - 1
  const changes: ContractChangePayload = change.changes

  const lastDateNotAvailable: Moment | undefined = currentContract.updatesRestrictedTo
    ? utcDateStringToMoment(currentContract.updatesRestrictedTo)
    : undefined
  const lastMonthNotAvailable = lastDateNotAvailable ? lastDateNotAvailable.month() : undefined
  const firstYearAvailable = lastDateNotAvailable ? lastDateNotAvailable.year() : undefined

  const editedMonth = editedDate ? new Date(editedDate).getUTCMonth() : undefined
  const editedYear = editedDate ? new Date(editedDate).getUTCFullYear() : undefined

  const targetBonusChangeScheduled = scheduledChanges.includes('bonus')
  const allChangesExceptBonusScheduled = (): boolean => {
    const allDefaultChangesScheduled = scheduledChanges.includes('employeeLevel') && scheduledChanges.includes('salary')
    if (hasNationalContractReferenceLevel) {
      return allDefaultChangesScheduled && scheduledChanges.includes('nationalReferenceLevel')
    } else if (hasGovernmentJobTitleReferenceLevel) {
      return allDefaultChangesScheduled && scheduledChanges.includes('governmentJobTitle')
    } else {
      return allDefaultChangesScheduled
    }
  }
  const onlyBonusChangeSchedulable =
    allChangesExceptBonusScheduled() &&
    (!targetBonusChangeScheduled || changes.bonus !== undefined) &&
    changes.employeeLevel === undefined &&
    changes.governmentJobTitle === undefined &&
    changes.nationalReferenceLevel === undefined &&
    changes.salary === undefined

  const monthMap: AvailableMonths[] = moment.months().map((month, index) => {
    return { month: index, text: month, year: currentYear }
  })

  if (currentMonth > 8) {
    // Oct-Nov-Dec
    monthMap.push(
      { month: 0, text: i18next.t('global.month.0'), year: nextYear },
      { month: 1, text: i18next.t('global.month.1'), year: nextYear },
      { month: 2, text: i18next.t('global.month.2'), year: nextYear },
    )
  } else if (currentMonth < 3) {
    // Jan-Feb-Mar
    monthMap.push(
      { month: 9, text: i18next.t('global.month.9'), year: previousYear },
      { month: 10, text: i18next.t('global.month.10'), year: previousYear },
      { month: 11, text: i18next.t('global.month.11'), year: previousYear },
    )
  }

  // Exclude months before updateRestrictedTo if bonus change is no longer schedulable
  let availableMonths: AvailableMonths[] =
    targetBonusChangeScheduled && changes.bonus === undefined && lastMonthNotAvailable && firstYearAvailable
      ? monthMap.filter(
          (item) => item.year > firstYearAvailable || (item.year === firstYearAvailable && item.month > lastMonthNotAvailable),
        )
      : monthMap

  // Exclude months of AS-1 if the meeting is AU and only bonus change is schedulable
  availableMonths =
    meeting.type === MeetingType.AppraisalUpdate && onlyBonusChangeSchedulable
      ? availableMonths.filter((item) => item.year >= meeting.appraisalSeason.year)
      : availableMonths

  // Exclude months before current contract validFrom
  const currentContractValidFrom = moment.utc(currentContract.validFrom).tz(employeeTimezone)
  availableMonths = availableMonths.filter((item) => {
    return (
      item.year > currentContractValidFrom.year() ||
      (item.year === currentContractValidFrom.year() && item.month >= currentContractValidFrom.month())
    )
  })

  // Exclude the months already scheduled except the month of the change that is being modified
  availableMonths = availableMonths.filter(
    (m) =>
      (m.month === editedMonth && m.year === editedYear) ||
      !datesOfCurrentChanges.some((c) => c.getUTCMonth() === m.month && c.getUTCFullYear() === m.year),
  )

  return availableMonths
}
